
import { FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { z } from "zod";

export const feedbackFormSchema = z.object({
  feedbackType: z.enum(["Bug", "Feature", "Improvement", "General"]),
  description: z.string()
    .min(10, { message: "Description must be at least 10 characters" })
    .max(1000, { message: "Description must be less than 1000 characters" }),
  consoleErrors: z.string()
    .max(10000, { message: "Console errors must be less than 10000 characters" })
    .optional(),
});

// Define the type for the form values
export type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

interface FeedbackFormFieldsProps {
  form: UseFormReturn<FeedbackFormValues>;
  feedbackTypes: Array<{ value: string; label: string }>;
}

export const FeedbackFormFields = ({ form, feedbackTypes }: FeedbackFormFieldsProps) => {
  const feedbackType = form.watch("feedbackType");

  return (
    <>
      <FormField
        control={form.control}
        name="feedbackType"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-primary">Feedback Type</FormLabel>
            <Select 
              onValueChange={field.onChange} 
              value={field.value}
            >
              <FormControl>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select feedback type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {feedbackTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormDescription>
              Select the category that best describes your feedback
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-primary">Description</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Please describe your feedback in detail..."
                className="min-h-32 resize-y"
                {...field}
              />
            </FormControl>
            <FormDescription>
              Be specific and include any relevant details
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {feedbackType === "Bug" && (
        <FormField
          control={form.control}
          name="consoleErrors"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-primary">Console Errors (Optional)</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Paste any console errors or stack traces here..."
                  className="min-h-32 resize-y font-mono text-sm"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                Copy errors from your browser dev tools so the description stays focused on what happened
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </>
  );
};
